class MediaQuery {
    constructor(query, onMatch, onMismatch) {
        const noop = () => {};

        onMatch = onMatch || noop;
        onMismatch = onMismatch || noop;

        this._mql = window.matchMedia(query);
        this._mql.addListener(this._update.bind(this));

        this._onMatch = onMatch;
        this._onMismatch = onMismatch;

        this._mql.matches ? onMatch() : onMismatch();
    }

    get matches() {
        return this._mql.matches;
    }

    _update() {
        this.matches ? this._onMatch() : this._onMismatch();
    }
}

class EventHelpers {
    debounce(fn, ms) {
        let timeout;

        return function debounce() {
            clearTimeout(timeout);
            timeout = setTimeout(() => fn.apply(null, arguments), ms);
        };
    }

    debounceStart(fn, ms) {
        let timeout;
        let fired = false;

        return function debounce() {
            if (!fired) {
                fired = true;
                fn.apply(null, arguments);
            }

            clearTimeout(timeout);
            timeout = setTimeout(() => (fired = false), ms);
        };
    }

    raf(fn) {
        let raf;

        return function _raf() {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => fn.apply(null, arguments));
        };
    }
}

class Sticky {
    constructor(element, options) {
        this.reflow = element => element.offsetTop;
        this.isNumber = n => !isNaN(n);
        this.scrollY = () => window.pageYOffset || window.scrollY;

        this.NOT_STICKY = 0;
        this.STICKY_TOP_FROM = 1;
        this.STICKY_TOP_UNTIL = 2;
        this.STICKY_BOTTOM_FROM = 3;
        this.STICKY_BOTTOM_UNTIL = 4;

        this._element = element;
        this._options = Object.assign(Sticky.options, options);

        this._element.style.zIndex = this._options.zIndex;

        this._sticky = this.NOT_STICKY;
        this._scrollTriggers = {};

        this._stickyTopUntil = document.querySelector(
            this._options.stickyTopUntil
        );
        this._stickyBottomUntil = document.querySelector(
            this._options.stickyBottomUntil
        );

        this._offsets = this._getOffsets();

        this._createPlaceholder();
        this._bindFunctions();

        // improvement: Maybe consider switching to a composable EventStream syntax to allow composing with other monitors
        this._options.mediaQuery
            ? new MediaQuery(this._options.mediaQuery, this._resume, this._halt)
            : this._resume();
    }

    // Usage

    // The Sticky component is NOT sticky by default
    // Specify whether is sticks to the top and or the bottom of the screen using data-sticky-top='true' and data-sticky-bottom='true'
    // Since we check for truthy values, data-sticky-top='false' will still evaluate to true
    // Use data-sticky-top-until='.selector-query' and data-sticky-bottom-until='.selector-query' to define boundaries for the sticky behaviour
    // Use data-offset-top='10' and data-offset-bottom='.selectory-query' to define an offset relative to the top or bottom of the window
    // A number value evaluates to a px value, a selector query will measure the height of the first matching element and use that value. This is useful when dealing with a fixed header for instance.

    static get options() {
        return {
            stickyTop: null, // 'Boolean' (thruthy value)
            stickyBottom: null, // 'Boolean' (thruthy value)
            stickyTopUntil: null, // 'Element'
            stickyBottomUntil: null, // 'Element'
            offsetTop: 0, // Comma seperated numbers and/or Elements
            offsetBottom: 0, // Comma seperated numbers and/or Elements
            zIndex: 10,
            stickClassName: "sticky",
            placeHolderClassName: "sticky-placeholder"
        };
    }

    _noStyles() {
        return {
            position: "",
            top: "",
            bottom: "",
            transform: ""
        };
    }

    _resume() {
        this._setupEvents(true);
        this._updateMetrics();
    }

    _halt() {
        this._setupEvents(false);
        this._unstick();
        this._element.style.width = "";
    }

    _bindFunctions() {
        this._resume = this._resume.bind(this);
        this._halt = this._halt.bind(this);
        this._updateMetrics = this._updateMetrics.bind(this);
        this._updateStickyness = this._updateStickyness.bind(this);

        const eventHelpers = new EventHelpers();
        this._onResize = eventHelpers.debounce(this._updateMetrics, 300);
        this._onScroll = eventHelpers.raf(this._updateStickyness);
        this._onScrollStart = eventHelpers.debounceStart(
            this._updateMetrics,
            1000
        );
    }

    _setupEvents(bind) {
        const action = bind ? "addEventListener" : "removeEventListener";

        window[action]("resize", this._onResize);
        window[action]("scroll", this._onScroll, { passive: true });
        window[action]("scroll", this._onScrollStart, { passive: true });
    }

    _createPlaceholder() {
        this._placeholder = document.createElement("div");
        this._placeholder.className = this._options.placeHolderClassName;
        this._placeholder.style.display = "none";

        this._element.parentNode.insertBefore(
            this._placeholder,
            this._element.nextSibling
        );
    }

    _isNumeric(num) {
        return !isNaN(num);
    }

    _isElementWithinViewPort() {
        const self = this;

        let offsetTopAndOrBottom = 0;
        if (
            self._options.offsetTop &&
            self._isNumeric(self._options.offsetTop)
        ) {
            offsetTopAndOrBottom += parseInt(self._options.offsetTop);
        }
        if (
            self._options.offsetBottom &&
            self._isNumeric(self._options.offsetBottom)
        ) {
            offsetTopAndOrBottom += parseInt(self._options.offsetBottom);
        }
        let elementHeightWithOffset =
            self._element.clientHeight + offsetTopAndOrBottom;

        return elementHeightWithOffset < window.innerHeight;
    }

    _updateStickyness() {
        const self = this;

        if (self._isElementWithinViewPort()) {
            if (
                this._options.stickyTop &&
                self.scrollY() > this._scrollTriggers.top.from &&
                self.scrollY() < this._scrollTriggers.top.until
            ) {
                return this._stickTopFrom();
            }

            if (
                this._options.stickyBottom &&
                self.scrollY() < this._scrollTriggers.bottom.from &&
                self.scrollY() > this._scrollTriggers.bottom.until
            ) {
                return this._stickBottomFrom();
            }

            if (
                this._options.stickyTop &&
                self.scrollY() > this._scrollTriggers.top.from &&
                self.scrollY() > this._scrollTriggers.top.until
            ) {
                return this._stickTopUntil();
            }

            if (
                this._options.stickyBottom &&
                self.scrollY() < this._scrollTriggers.bottom.from &&
                self.scrollY() < this._scrollTriggers.bottom.until
            ) {
                return this._stickBottomUntil();
            }
        }

        this._unstick();
    }

    _updateMetrics() {
        this._unstick();
        this._element.style.width = "";

        this.reflow(this._element);
        this.reflow(this._placeholder);

        this._element.style.width = this._element.offsetWidth + "px";

        this._placeholder.style.width = this._element.offsetWidth + "px";
        this._placeholder.style.height = this._element.offsetHeight + "px";

        this._scrollTriggers = {
            top: {
                from: this._scrollTriggerTopFrom(),
                until: this._scrollTriggerTopUntil()
            },
            bottom: {
                from: this._scrollTriggerBottomFrom(),
                until: this._scrollTriggerBottomUntil()
            }
        };

        this._updateStickyness();
    }

    _stickTopFrom() {
        if (this._sticky === this.STICKY_TOP_FROM) {
            return;
        }

        this._sticky = this.STICKY_TOP_FROM;

        this._stickFrom("top");
    }

    _stickTopUntil() {
        if (this._sticky === this.STICKY_TOP_UNTIL) {
            return;
        }

        this._sticky = this.STICKY_TOP_UNTIL;

        this._stickUntil("top");
    }

    _stickBottomFrom() {
        if (this._sticky === this.STICKY_BOTTOM_FROM) {
            return;
        }

        this._sticky = this.STICKY_BOTTOM_FROM;

        this._stickFrom("bottom");
    }

    _stickBottomUntil() {
        if (this._sticky === this.STICKY_BOTTOM_UNTIL) {
            return;
        }

        this._sticky = this.STICKY_BOTTOM_UNTIL;

        this._stickUntil("bottom");
    }

    _stickFrom(pos) {
        // bottom
        const styles = {
            position: "fixed"
        };

        styles[pos] = this._offset(pos) + "px";

        Object.assign(this._element.style, this._noStyles(), styles);

        this._placeholder.style.display = "block";
        this._element.classList.add(this._options.stickClassName);
    }

    _stickUntil(pos) {
        const translation =
            this._scrollTriggers[pos].until - this._scrollTriggers[pos].from;

        Object.assign(this._element.style, this._noStyles(), {
            position: "absolute",
            transform: `translateY(${translation}px)`
        });

        this._placeholder.style.display = "block";
        this._element.classList.add(this._options.stickClassName);
    }

    _unstick() {
        if (this._sticky === this.NOT_STICKY) {
            return;
        }

        this._sticky = this.NOT_STICKY;

        Object.assign(this._element.style, this._noStyles());

        this._placeholder.style.display = "none";
        this._element.classList.remove(this._options.stickClassName);
    }

    _offset(pos) {
        const self = this;
        return this._offsets[pos].reduce(
            (result, o) => result + (self.isNumber(o) ? o : o.offsetHeight),
            0
        );
    }

    _scrollTriggerTopFrom() {
        const self = this;

        if (!this._options.stickyTop) {
            return Infinity;
        }

        return (
            this._element.getBoundingClientRect().top +
            self.scrollY() -
            this._offset("top")
        );
    }

    _scrollTriggerTopUntil() {
        const self = this;

        if (!this._stickyTopUntil) {
            return Infinity;
        }

        return (
            this._stickyTopUntil.getBoundingClientRect().top +
            self.scrollY() -
            this._element.offsetHeight -
            this._offset("top") -
            this._offset("bottom")
        );
    }

    _scrollTriggerBottomUntil() {
        const self = this;

        if (!this._stickyBottomUntil) {
            return -Infinity;
        }

        return (
            this._stickyBottomUntil.getBoundingClientRect().bottom +
            self.scrollY() +
            this._element.offsetHeight -
            window.innerHeight +
            this._offset("top") +
            this._offset("bottom")
        );
    }

    _scrollTriggerBottomFrom() {
        const self = this;

        if (!this._options.stickyBottom) {
            return -Infinity;
        }

        return (
            this._element.getBoundingClientRect().bottom +
            self.scrollY() -
            window.innerHeight +
            this._offset("bottom")
        );
    }

    _getOffsets() {
        const self = this;

        const offsets = {
            top: [],
            bottom: []
        };

        if (this._options.offsetTop) {
            offsets.top = this._options.offsetTop
                .split(",")
                .map(o => (self.isNumber(o) ? +o : document.querySelector(o)));
        }

        if (this._options.offsetBottom) {
            offsets.bottom = this._options.offsetBottom
                .split(",")
                .map(o => (self.isNumber(o) ? +o : document.querySelector(o)));
        }

        return offsets;
    }
}

document.addEventListener("DOMContentLoaded", function(event) {
    const stickyModuleName = "ui/Sticky";
    for (let element of document.querySelectorAll(
        `[data-module="${stickyModuleName}"]`
    )) {
        const options = JSON.parse(JSON.stringify(element.dataset));
        new Sticky(element, options);
        element.setAttribute("data-initialized", "");
    }
});
